import React, { useCallback, useEffect, useRef, useState } from 'react';
import algoliasearch from 'algoliasearch/lite';
import { InstantSearch } from 'react-instantsearch-dom';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash.isempty';
import styled from 'styled-components';

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import InfiniteHitsWithScrollToTop from '../components/InfiniteHitsWithScrollToTop/InfiniteHitsWithScrollToTop';
import GTTFlow from '../components/GTT/GTTFlow';
import BuyFlow from '../components/Buy/BuyFlow';
import {
  ENTRY_POINTS,
  trackGttBuyStartedBanner,
} from '../components/GTT/Common/gtmEventHelper';
import {
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../util/helpers/sessionStorageHelper';
import {
  deleteWizardKey,
  setWizardContent,
} from '../util/helpers/wizardHelpers';
import { useSessionStorageState } from '../util/hooks/useSessionStorageState';
import {
  IS_BUY_PAGE_BUY_FOR_STORAGE_OPEN,
  IS_BUY_TRIGGER_ORDER_OPEN,
  TRIGGER_ORDER_WIZARD,
  WIZARD_DATA,
} from '../constants/sessionStorage';
import SortBy from '../components/SortBy/SortBy';
import {
  storePriceAlertDeepLinkFromSearch,
  PRICE_ALERT_DEEP_LINK_PARAMS,
} from '../util/helpers/priceAlertDeepLinkHelper';

import config from '../config';
import Section from '../components/Section/Section';
import Filter from '../components/Filter/Filter';
import Button from '../components/Button/Button';
import { ReactComponent as FilterIcon } from '../assets/images/svg/filter.svg';
import { ReactComponent as Sparkle } from '../assets/images/svg/sparkle.svg';
import {
  mediaBelow,
  mediaBetween,
  pxToRem,
  pxToRemMd,
} from '../assets/styles/helper';
import { variables } from '../assets/styles/variables';
import themeColors from '../assets/styles/themeColors';
import useWindowSize from '../util/hooks/useIsMobileHook';
import { fetchProductsWithPrices } from '../store/actions/products/productActions';
import {
  getCurrentAccount,
  selectIsPriceAlertEligibleAccount,
  selectNagDashboard,
} from '../store/selectors/accountSelectors';
import { selectAuthUser } from '../store/selectors/userSelectors';
import { isAccountVerified } from '../constants/nagDashboardContants';
import { accountTypeKeys } from '../util/enum/api/accountTypes';
import { parseEnumType } from '../util/helpers/enumMappers';
import useTriggerOrderVerification from '../util/hooks/useTriggerOrderVerification';
import { useIsClosedAccount } from '../util/hooks/useIsClosedAccount';
import TriggerOrderBlockModal from '../components/GTT/TriggerOrderBlockModal';
import useGetPeriodicProductCodes from '../util/hooks/useGetPeriodicProductCodes';
import FilterModal from '../components/Modal/FilterModal';
import { selectIsLoadingByActionType } from '../store/selectors/loadingSelectors';
import { PRODUCT_PRICES_LOADING } from '../store/actions/products/productActionConstants';
import useGtmHook from '../util/hooks/useGtmHook';
import BlockSectionLoader from '../components/Loader/BlockSectionLoader';
import SpotPricesBar from '../components/Buy/SpotPricesBar';
import {
  selectProductPrices,
  selectProductLocationsError,
} from '../store/selectors/productsSelector';
import { selectSettings } from '../store/selectors/settingsSelectors';
import PriceTierModal from '../components/Modals/Prices/PriceTierModal';
import { ProductSearch } from '../components/InputFields/ProductSearch';
import TradingContainer from '../components/Trading/TradingContainer';
import TradingFilter from '../components/Trading/TradingFilter';
import TradingContent from '../components/Trading/TradingContent';
import TradingHeader from '../components/Trading/TradingHeader';
import { TradingFilterButton } from '../components/Trading/TradingFilterButton';
import VirtualFilter from '../components/Filter/VirtualFilters';
import NagDashboardBanner from '../components/NagDashboard/NagDashboardBanner';
import { ACCOUNT_DATA_LOADED } from '../constants/gtmEvents';
import { COUNTDOWN_TIMER_INTERVAL_MS } from '../constants/timerConstants';
import ComingSoonBanner from '../components/ComingSoon/ComingSoon';
import LaunchBanner from '../components/LaunchBanner/LaunchBanner';
import {
  setupMockPriceAlerts,
  fetchPriceAlerts,
} from '../store/actions/priceAlerts/priceAlertsActions';
// import MapleLeafBanner from '../components/MapleLeafBanner/MapleLeafBanner';

const { apiKey, apiSecret, apiBuyIndex } = config.algoliaSearch;
const searchClient = algoliasearch(apiKey, apiSecret);

const BuyPageLayout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${pxToRem(16)};
`;

const TestPriceAlertsButton = styled(Button)`
  background-color: ${themeColors.colorDashBorderButton};
`;

const TradingHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: ${pxToRem(16)};

  ${TradingHeader} {
    margin: 0;
  }

  ${mediaBelow(variables.breakpoints.bpXl)} {
    flex-direction: column;
    align-items: stretch;
    gap: ${pxToRemMd(6)};

    ${TradingHeader} {
      order: 1;
      max-width: 100%;
      margin-left: 0;
    }

    > button {
      order: 2;
      width: 100%;
    }
  }

  ${mediaBetween(variables.breakpoints.bpMd, variables.breakpoints.bpXl)} {
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;

    ${TradingHeader} {
      order: 2;
      max-width: min-content;
      margin-left: 0;

      > div {
        margin-bottom: 0;
      }
    }

    > button {
      order: 1;
      width: auto;
    }
  }
`;

const BuyAtTargetPriceCard = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  gap: ${pxToRemMd(16)};
  margin: ${pxToRemMd(16)};
  padding: ${pxToRemMd(20)};
  border: 1px solid ${themeColors.colorTextSecondary};
  border-radius: ${variables.borderRadius.borderRadius};
  font-size: ${pxToRemMd(16)};
  font-weight: 600;
  color: ${themeColors.colorTextPrimary};
`;

const BuyAtTargetPriceCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${pxToRemMd(8)};
`;

const BuyAtTargetPriceCardBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${pxToRem(4)};
  padding: ${pxToRem(2)} ${pxToRem(8)};
  border-radius: ${variables.borderRadius.borderRadius};
  background-color: ${themeColors.colorGTTNew};
  color: ${themeColors.colorIconTertiary};
  font-size: ${pxToRem(12)};
  font-weight: 600;
  line-height: 1.4;

  svg {
    width: ${pxToRem(12)};
    height: ${pxToRem(12)};
  }
`;

// The desktop CTA on the right of the spot-price bar above the toolbar row.
// The NEW badge sits on the button's top-left corner instead of inside it.
const BuyAtTargetPriceCta = styled.div`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;

  button {
    min-width: ${pxToRem(208)};
    padding: 0 ${pxToRem(24)};
  }
`;

// Figma: 44x18, radius 3, rotated -8deg (Figma turns counter-clockwise, so
// that is CSS rotate(8deg)), 10px/700 label, hung off the button's
// top-left corner.
const BuyAtTargetPriceCtaBadge = styled.span`
  position: absolute;
  top: ${pxToRem(-9)};
  left: ${pxToRem(-12)};
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${pxToRem(44)};
  height: ${pxToRem(18)};
  border-radius: ${pxToRem(3)};
  background-color: ${themeColors.colorGTTNew};
  color: ${themeColors.colorPrimary};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  transform: rotate(8deg);
  pointer-events: none;
`;

const BuyPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPriceTiersOpen, setIsPriceTiersOpen] = useState(false);
  const [priceTiersData, setPriceTiersData] = useState({});
  const windowSize = useWindowSize();
  const account = useSelector(getCurrentAccount);
  const authUser = useSelector(selectAuthUser);
  const nagDashboard = useSelector(selectNagDashboard);
  const isClosedAccount = useIsClosedAccount();
  const { gtmScreenView, appGtmEvent, ecommerceGtmEvent } = useGtmHook();
  const isMobile = windowSize.width < 1200;
  const [products, setProducts] = useState(null);
  const [priceTiersProduct, setPriceTiersProduct] = useState(null);
  const isLoadingProducts = useSelector(
    selectIsLoadingByActionType(PRODUCT_PRICES_LOADING),
  );
  const productPrices = useSelector(selectProductPrices);
  const productPricesError = useSelector(selectProductLocationsError);
  const isPriceAlertEligibleAccount = useSelector(
    selectIsPriceAlertEligibleAccount,
  );
  const {
    isTriggerOrderEntryHidden,
    isReadOnly,
    verificationVariant,
    closeVerification,
    blockIfIneligible,
  } = useTriggerOrderVerification();
  const settings = useSelector(selectSettings);
  const [searchState, setSearchState] = useState({});
  const location = useLocation();
  const [isLockedPrice, setIsLockedPrice] = useState(false);
  const CoomingSoonBannerRequired = settings?.CoomingSoonBannerRequired;
  const isTriggerOrderActive = settings?.IsTriggerOrderActive;

  const { WelcomeBannerRequired } = settings;
  const { brokerTheme } = config;
  const isHAA = brokerTheme === 'HardAssetsAlliance';
  const isGS = brokerTheme === 'GoldSilver';

  // Persist the Buy-page GTT modal so a refresh reopens it on the same step with
  // the same values (mirrors the buy modal's session-storage persistence).
  const [isGttOpen, setIsGttOpenState] = useSessionStorageState(
    IS_BUY_TRIGGER_ORDER_OPEN,
    false,
  );
  const setIsGttOpen = (value) => {
    setWizardContent(IS_BUY_TRIGGER_ORDER_OPEN, value);
    setIsGttOpenState(value);
  };
  // Persist the Buy modal opened via the GTT->Buy hand-off so a refresh reopens
  // it (its WIZARD_DATA is already seeded by handleSwitchToBuyFlow).
  const [
    isBuyForStorageOpen,
    setIsBuyForStorageOpenState,
  ] = useSessionStorageState(IS_BUY_PAGE_BUY_FOR_STORAGE_OPEN, false);
  const setIsBuyForStorageOpen = (value) => {
    setWizardContent(IS_BUY_PAGE_BUY_FOR_STORAGE_OPEN, value);
    setIsBuyForStorageOpenState(value);
  };

  // Open the GTT flow from its first (product-selection) step. Clear any stale
  // GTT wizard data/step first so it always starts fresh with no preselection.
  const openBuyAtTargetPrice = () => {
    // Ineligible accounts see the CTA but are blocked with a popup on click.
    if (blockIfIneligible()) {
      return;
    }
    storeInSessionStorage(WIZARD_DATA, {
      entryPoint: ENTRY_POINTS.BUY_PAGE,
    });
    deleteWizardKey(TRIGGER_ORDER_WIZARD);
    setIsGttOpen(true);
  };

  // The desktop CTA at the start of the toolbar row. It used to be the header's
  // "Buy at Target Price" nav item, and behaves exactly as that did: same
  // gate, same gtt_buy_started_banner event, same top_nav_banner entry point
  // (so TargetPriceProductSelection's isBannerFlow still gates
  // gtt_buy_sku_search / gtt_buy_sku_selected), and always a fresh journey,
  // whatever step/product a previous GTT<->Buy hand-off left behind. The mobile
  // card above keeps its own handler (openBuyAtTargetPrice).
  const openBuyAtTargetPriceFromStart = () => {
    // Ineligible accounts see the CTA but are blocked with a popup on click.
    if (blockIfIneligible()) {
      return;
    }
    trackGttBuyStartedBanner({
      ecommerceGtmEvent,
      userId: authUser?.UserUid,
      accountType: parseEnumType(accountTypeKeys, account?.AccountType),
      verified: isAccountVerified(nagDashboard),
    });
    storeInSessionStorage(WIZARD_DATA, {
      entryPoint: ENTRY_POINTS.TOP_NAV_BANNER,
    });
    deleteWizardKey(TRIGGER_ORDER_WIZARD);
    setIsGttOpen(true);
  };

  // "Buy at Current Price?" hand-off from the GTT order-entry screen: close GTT
  // (clearing its step), seed the Buy-flow shape into WIZARD_DATA, then open the
  // regular Buy flow with the same product, vault and quantity. Stashes the GTT
  // journey's entry point under its own gttEntryPoint key (not Buy's own
  // entryPoint, which has a separate vocabulary and drives Buy's own GTM events
  // — see PriceAlerts/Common/gtmEventHelper's ENTRY_POINTS), so a later "Buy at
  // Target Price?" switch back to GTT can still report the journey's real
  // origin instead of a generic default, without Buy's events picking up a
  // GTT-only value.
  const handleSwitchToBuyFlow = (product, vault, quantity) => {
    setIsGttOpen(false);
    deleteWizardKey(TRIGGER_ORDER_WIZARD);
    const existingWizardData = retrieveFromSessionStorage(WIZARD_DATA) || {};
    storeInSessionStorage(WIZARD_DATA, {
      storageProduct: product,
      selectedVault: vault,
      orderValues: { QuantityAmount: quantity, CashAmount: 0 },
      activeType: 'quantity',
      ...(existingWizardData.entryPoint != null && {
        gttEntryPoint: existingWizardData.entryPoint,
      }),
    });
    setIsBuyForStorageOpen(true);
  };

  // "Buy at Target Price?" hand-off from the Buy order-selection screen: close
  // the Buy flow, seed the GTT-flow shape into WIZARD_DATA, land on the GTT
  // order-entry step (index 1) so the product carries over, then open the GTT
  // flow. Mirror of handleSwitchToBuyFlow. Preserves the GTT journey's own
  // gttEntryPoint (stashed by handleSwitchToBuyFlow above on a prior round
  // trip) — not Buy's entryPoint, which may hold a Buy-vocabulary value that
  // isn't a valid GTT entry point — falling back to BUY_PAGE only when
  // there's no prior GTT entry point to restore.
  const handleSwitchToTargetPrice = (product, vault, quantity) => {
    // Same permission gate as openBuyAtTargetPrice — this link is another
    // entry point into GTT, so an ineligible user could otherwise bypass the
    // main CTA's restriction by switching over from inside the Buy flow.
    if (blockIfIneligible()) {
      return;
    }
    setIsBuyForStorageOpen(false);
    const existingWizardData = retrieveFromSessionStorage(WIZARD_DATA) || {};
    storeInSessionStorage(WIZARD_DATA, {
      entryPoint: existingWizardData.gttEntryPoint ?? ENTRY_POINTS.BUY_PAGE,
      selectedProduct: product,
      ...(vault && vault.LocationType !== undefined
        ? { selectedVault: vault }
        : {}),
      ...(quantity ? { orderValues: { QuantityAmount: quantity } } : {}),
    });
    setWizardContent(TRIGGER_ORDER_WIZARD, 1);
    setIsGttOpen(true);
  };

  const priceTiersClickHandler = useCallback((product, location) => {
    setIsPriceTiersOpen(true);
    setPriceTiersProduct(product);
    setPriceTiersData(location);
  }, []);

  const [counterValue, setCounterValue] = useState(
    Math.floor(
      settings.PricingRefreshFrequencyMilliseconds /
        COUNTDOWN_TIMER_INTERVAL_MS,
    ),
  );
  const [unityValue, setUnityValue] = useState(false);

  const periodicCallback = useCallback((epoch) => {
    if (epoch?.data) {
      const [counter, unity] = epoch.data;
      setCounterValue((prevCounter) => prevCounter - prevCounter + counter);
      setUnityValue(unity);
    }
  }, []);

  useGetPeriodicProductCodes(
    'buy',
    account,
    'Buy',
    // Use !isEmpty so an empty [] (e.g. the ineligible/error resolved state)
    // does NOT start the periodic price poller and re-fire failing calls.
    !isEmpty(products) ||
      (!isEmpty(products) && products.length && settings?.IsLockedPriceActive),
    settings?.PricingRefreshFrequencyMilliseconds,
    !isLockedPrice ? periodicCallback : null,
  );

  const getPrices = () => {
    dispatch(
      fetchProductsWithPrices({
        accountUid: account.AccountUid,
        side: 'Buy',
      }),
    );
  };

  useEffect(() => {
    gtmScreenView({
      path: window.location.pathname,
      title: 'Buy',
    });
    appGtmEvent(ACCOUNT_DATA_LOADED);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!isEmpty(account) && account.AccountUid) {
      getPrices();
    }
  }, [dispatch, account]); //eslint-disable-line

  // Track that a prices request has actually been dispatched, so the loader
  // resolution below can't fire on the initial render (before the call starts),
  // only after it settles.
  const pricesRequestedRef = useRef(false);
  useEffect(() => {
    if (isLoadingProducts) {
      pricesRequestedRef.current = true;
    }
  }, [isLoadingProducts]);

  // Resolve the product-list loader once the prices call settles with no usable
  // data, instead of leaving the spinner hanging. The BE can reject an
  // ineligible account (e.g. pending/under-review) without a parseable error
  // message, so don't depend on productPricesError being populated — keying off
  // the request having settled (loading finished, no prices) also covers that.
  useEffect(() => {
    if (
      pricesRequestedRef.current &&
      !isLoadingProducts &&
      products === null &&
      (productPricesError || isEmpty(productPrices))
    ) {
      setProducts([]);
    }
  }, [productPricesError, isLoadingProducts, products, productPrices]);

  useEffect(() => {
    if (!settings || isEmpty(settings)) {
      return;
    }

    setIsLockedPrice(settings?.IsLockedPricingActive);
  }, [settings]);

  // Price Alert deep link (email/SMS), for direct logged-in navigation:
  //   /buy?openAlerts=active|expired|cancelled&productCode=&priceAlertUid=&accountUid=
  //   /buy?openBuy=true&productCode=&priceAlertUid=&accountUid=
  // Persist the target, then strip the params so a refresh won't reopen the
  // modal. (When arriving via login, landingPageHelper has already consumed and
  // cleaned these before the redirect, so location.search is already clean.)
  useEffect(() => {
    const matched = storePriceAlertDeepLinkFromSearch(location.search);
    if (!matched) return;
    const params = new URLSearchParams(location.search);
    PRICE_ALERT_DEEP_LINK_PARAMS.forEach((key) => params.delete(key));
    const newSearch = params.toString();
    window.history.replaceState(
      null,
      '',
      `${location.pathname}${newSearch ? `?${newSearch}` : ''}`,
    );
  }, []); // eslint-disable-line

  // Desktop only: mobile keeps its own card below the toolbar.
  const buyAtTargetPriceCta =
    !isMobile &&
    !isClosedAccount &&
    isTriggerOrderActive &&
    !isTriggerOrderEntryHidden ? (
      <BuyAtTargetPriceCta>
        {/* colorGTTNew exists only in the GoldSilver theme; elsewhere the pill
            would have no background and sit unreadable on the button edge. */}
        {isGS && (
          <BuyAtTargetPriceCtaBadge>
            {t('gtt.nav.new')}
          </BuyAtTargetPriceCtaBadge>
        )}
        <Button
          height="38px"
          variant="primaryOutlined"
          textTransform="none"
          fontSize="16px"
          lockStyle={isReadOnly}
          onClick={openBuyAtTargetPriceFromStart}
          data-cy="button-buy-at-target-price-start"
        >
          {t('gtt.common.buyAtTargetPrice')}
        </Button>
      </BuyAtTargetPriceCta>
    ) : null;

  // The strip renders only with prices (GS) or the CTA; the tighter spacing
  // under it applies only then.
  const hasSpotPricesBar = isGS || Boolean(buyAtTargetPriceCta);

  return (
    <>
      <SpotPricesBar showPrices={isGS} action={buyAtTargetPriceCta} />
      {/* {isGS && <MapleLeafBanner />} */}
      <NagDashboardBanner />
      {isHAA && WelcomeBannerRequired && <LaunchBanner />}
      {isHAA && CoomingSoonBannerRequired && <ComingSoonBanner />}
      <BuyPageLayout>
        <Section style={{ flex: 1, minWidth: 0 }}>
          <TradingContainer
            css={hasSpotPricesBar ? `margin-top: ${pxToRem(28)};` : undefined}
          >
            <InstantSearch
              indexName={apiBuyIndex}
              searchClient={searchClient}
              searchState={searchState}
              onSearchStateChange={setSearchState}
            >
              {isMobile && <VirtualFilter />}
              {!isMobile && (
                <TradingFilter data-cy="container-filters">
                  {products === null ? (
                    <BlockSectionLoader isLoading />
                  ) : (
                    <Filter products={products} productPrices={productPrices} />
                  )}
                </TradingFilter>
              )}
              <TradingContent data-cy="container-product-list">
                <TradingHeaderRow>
                  {settings?.EnableMocks && isPriceAlertEligibleAccount && (
                    <TestPriceAlertsButton
                      size="lg"
                      variant="secondaryDashed"
                      textTransform="capitalize"
                      onClick={() => {
                        dispatch(
                          setupMockPriceAlerts({
                            accountUid: account.AccountUid,
                            onSuccess: () => {
                              dispatch(
                                fetchPriceAlerts({
                                  accountUid: account.AccountUid,
                                  onSuccess: getPrices,
                                }),
                              );
                            },
                          }),
                        );
                      }}
                    >
                      {t('gtt.common.testPriceAlerts')}
                    </TestPriceAlertsButton>
                  )}
                  <TradingHeader>
                    <div>
                      <SortBy
                        items={[
                          {
                            value: apiBuyIndex,
                            label: t('sortBy.popularity'),
                            dataCy: 'menu-item-product-sort-popularity',
                          },
                          {
                            value: `${apiBuyIndex}_prices_asc`,
                            label: t('sortBy.lowToHigh'),
                            dataCy: 'menu-item-product-sort-low-to-high',
                          },
                          {
                            value: `${apiBuyIndex}_prices_desc`,
                            label: t('sortBy.highToLow'),
                            dataCy: 'menu-item-product-sort-high-to-low',
                          },
                        ]}
                        defaultRefinement={apiBuyIndex}
                        getPrices={getPrices}
                      />
                      <ProductSearch data-cy="input-product-search" />
                      <TradingFilterButton
                        onClick={() => {
                          setIsModalOpen(true);
                        }}
                      >
                        <FilterIcon />
                      </TradingFilterButton>
                    </div>
                  </TradingHeader>
                </TradingHeaderRow>

                {isMobile &&
                  isTriggerOrderActive &&
                  !isTriggerOrderEntryHidden && (
                    <BuyAtTargetPriceCard data-cy="card-buy-at-target-price">
                      <BuyAtTargetPriceCardHeader>
                        {t('gtt.nav.wantToBuyLower')}
                        <BuyAtTargetPriceCardBadge>
                          <Sparkle />
                          {t('gtt.nav.new')}
                        </BuyAtTargetPriceCardBadge>
                      </BuyAtTargetPriceCardHeader>
                      <Button
                        variant="primary"
                        isWidthS
                        textTransform="none"
                        lockStyle={isReadOnly}
                        onClick={openBuyAtTargetPrice}
                        data-cy="button-buy-at-target-price"
                      >
                        {t('gtt.common.buyAtTargetPrice')}
                      </Button>
                    </BuyAtTargetPriceCard>
                  )}
                <InfiniteHitsWithScrollToTop
                  priceTiersClickHandler={priceTiersClickHandler}
                  page="buy"
                  side="buy"
                  setProducts={setProducts}
                  products={products}
                  isLoading={isLoadingProducts}
                  productCode={location?.state?.productCode}
                  isLockedPrice={isLockedPrice}
                  counter={counterValue}
                  unity={unityValue}
                />
              </TradingContent>
              <PriceTierModal
                isOpen={isPriceTiersOpen}
                data={priceTiersData}
                account={account}
                tradingSide="Buy"
                handleClose={() => setIsPriceTiersOpen(false)}
                handleBack={() => setIsPriceTiersOpen(false)}
                priceTiersProduct={priceTiersProduct}
              />
              {isMobile && (
                <FilterModal
                  title="Filters"
                  isOpen={isModalOpen && isMobile}
                  close={setIsModalOpen}
                  size="sm"
                >
                  <SortBy
                    items={[
                      {
                        value: apiBuyIndex,
                        label: t('sortBy.popularity'),
                        dataCy: 'menu-item-product-sort-popularity-mobile',
                      },
                      {
                        value: `${apiBuyIndex}_prices_asc`,
                        label: t('sortBy.lowToHigh'),
                        dataCy: 'menu-item-product-sort-low-to-high-mobile',
                      },
                      {
                        value: `${apiBuyIndex}_prices_desc`,
                        label: t('sortBy.highToLow'),
                        dataCy: 'menu-item-product-sort-high-to-low-mobile',
                      },
                    ]}
                    defaultRefinement={apiBuyIndex}
                    getPrices={getPrices}
                  />
                  {products === null ? (
                    <BlockSectionLoader isLoading />
                  ) : (
                    <>
                      <InstantSearch
                        indexName={apiBuyIndex}
                        searchClient={searchClient}
                        searchState={searchState}
                        onSearchStateChange={setSearchState}
                      >
                        <Filter
                          close={setIsModalOpen}
                          products={products}
                          productPrices={productPrices}
                        />
                      </InstantSearch>
                    </>
                  )}
                </FilterModal>
              )}
            </InstantSearch>
          </TradingContainer>
        </Section>
        {isGttOpen && (
          <GTTFlow
            isModalOpen={isGttOpen}
            handleCloseModal={() => setIsGttOpen(false)}
            onSwitchToBuyFlow={handleSwitchToBuyFlow}
          />
        )}
        <TriggerOrderBlockModal
          variant={verificationVariant}
          close={closeVerification}
        />
        {isBuyForStorageOpen && (
          <BuyFlow
            isModalOpen={isBuyForStorageOpen}
            handleCloseModal={() => setIsBuyForStorageOpen(false)}
            onSwitchToTargetPrice={
              isTriggerOrderActive && !isTriggerOrderEntryHidden
                ? handleSwitchToTargetPrice
                : undefined
            }
          />
        )}
      </BuyPageLayout>
    </>
  );
};

export default BuyPage;
